import { useEditor, useNode } from '@craftjs/core';
import { useInspectorNodeContext } from '@/context/InspectorNodeContext';

/**
 * Custom hook that provides node data for inspector components.
 * Automatically handles the InspectorNodeContext to allow targeting
 * a specific node (e.g., parent Form) instead of the currently selected node.
 *
 * @param selector Optional selector function to extract specific props from node data
 * @returns Object containing id, props, node reference, and editor actions
 *
 * @example
 * // Basic usage - get all props
 * const { id, props, actions } = useInspectorNode();
 *
 * @example
 * // With selector - get specific props
 * const { id, props, actions } = useInspectorNode((props) => ({
 *   borderColor: props.borderColor,
 *   borderWidth: props.borderWidth,
 * }));
 */
export function useInspectorNode<T = Record<string, unknown>>(
  selector?: (props: Record<string, unknown>) => T,
) {
  const { targetNodeId } = useInspectorNodeContext();

  // Get currently selected node data
  const selectedNodeData = useNode((node) => {
    const props = node.data.props || {};
    return {
      selectedId: node.id,
      selectedProps: selector ? selector(props) : (props as T),
      selectedNode: node,
    };
  });

  // Get target node data (if context provides one) in a single useEditor call
  const { targetProps, targetNode, hasTargetNode } = useEditor((state, q) => {
    let targetData: T | undefined;
    let targetNodeRef = undefined;
    let hasTarget = false;

    if (targetNodeId && state.nodes[targetNodeId]) {
      const node = q.node(targetNodeId).get();
      if (node) {
        const props = node.data.props || {};
        targetData = selector ? selector(props) : (props as T);
        targetNodeRef = node;
        hasTarget = true;
      }
    }

    return {
      targetProps: targetData,
      targetNode: targetNodeRef,
      hasTargetNode: hasTarget,
    };
  });

  // Get actions and query separately
  const { actions: editorActions, query } = useEditor();

  // Determine which data to use
  const id = targetNodeId || selectedNodeData.selectedId;
  const props = hasTargetNode ? (targetProps as T) : selectedNodeData.selectedProps;
  const node = hasTargetNode ? targetNode : selectedNodeData.selectedNode;

  return {
    /** The node ID to use for setProp calls */
    id,
    /** The effective props (from target node or selected node) */
    props,
    /** The node reference */
    node,
    /** Whether we're targeting a specific node via context */
    isTargetingContextNode: hasTargetNode,
    /** Editor actions for setProp, etc. */
    actions: editorActions,
    /** Editor query for additional lookups */
    query,
  };
}

/**
 * Simplified version that only returns id and actions.
 * Use this when you don't need to read props from the node.
 */
export function useInspectorNodeId() {
  const { targetNodeId } = useInspectorNodeContext();
  const { id: selectedId } = useNode();
  const { actions } = useEditor();

  return {
    id: targetNodeId || selectedId,
    actions,
  };
}
